import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createTravelOrder } from '@/lib/checkout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

export default function PassengerDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const offer = location.state?.offer;
  const search = location.state?.search;
  
  const [isLoading, setIsLoading] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="mb-4">No offer selected</p>
          <Button onClick={() => navigate('/travel/search')}>
            Start New Search
          </Button>
        </div>
      </div>
    );
  }

  const totalAmount = Number(offer.total_amount || offer.total || 0);

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    
    if (!contactName || !contactEmail) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Create passengers array based on search adults count
      const adultsCount = search?.adults || 1;
      const passengers = Array.from({ length: adultsCount }, (_, i) => ({
        type: 'adult',
        given_name: i === 0 ? contactName.split(' ')[0] : 'Passenger',
        family_name: i === 0 ? contactName.split(' ').slice(1).join(' ') || contactName : `${i + 1}`,
      }));

      const { orderId } = await createTravelOrder({
        offerId: offer.id,
        passengers,
        totalAmount, // Pass the actual offer amount
        contact: {
          name: contactName,
          email: contactEmail,
          phone: contactPhone,
        },
      });

      navigate(`/travel/checkout?orderId=${orderId}`);
    } catch (err: any) {
      console.error(err);
      toast({
        title: 'Could not create booking',
        description: err?.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-2xl font-heading font-semibold mb-6 text-flexaura-navy">
          Passenger Details
        </h1>

        <div className="bg-card border rounded-2xl p-6 card-shadow mb-6">
          <h2 className="text-lg font-medium mb-4">Selected Flight</h2>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              {offer.slices?.map((s: any, i: number) => (
                <span key={i}>
                  {s?.origin?.iata_code} → {s?.destination?.iata_code}
                  {i < offer.slices.length - 1 ? ' • ' : ''}
                </span>
              ))}
            </p>
            <p className="text-lg font-semibold text-flexaura-navy">
              AED {totalAmount.toLocaleString()}
            </p>
          </div>
        </div>

        <form onSubmit={handleContinue} className="bg-card border rounded-2xl p-6 card-shadow space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">
              Full Name *
            </label>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="John Doe"
              required
              className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">
              Email *
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="john@example.com"
              required
              className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">
              Phone
            </label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+971501234567"
              className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition"
            />
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full py-6 text-base"
            >
              {isLoading ? 'Creating booking...' : 'Continue to payment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
